import {
  ChartLineUp,
  ChatCircleText,
  Check,
  ListChecks,
  Sparkle,
} from "@phosphor-icons/react";
import { Badge, Box, Button, Flex, Heading, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

/**
 * Vitalora demo agent activity feed (S6 of the build brief).
 *
 * Static client-side render of the seeded `IntegrationEvent` rows in
 * `packages/commons-node/src/seed/vitalora/integrationEvents.ts`. We
 * render these from a constant rather than fetching them so:
 *   1) the page loads in a single paint (the brief's <3s load target),
 *   2) reset doesn't blank the screen between demos, and
 *   3) the strings stay aligned with the scripted Ask Puntt response in
 *      `services/christo/app/handlers/catalyst/demoAskPunttResponses.ts`.
 *
 * The workload-rebalance card lives at the bottom — clicking Confirm
 * moves it to the "Done" group locally so the demoer can show the
 * before/after states in the same flow without round-tripping.
 */
interface ActivityRow {
  time: string;
  title: string;
  detail: string;
  channel: "Slack" | "Wrike" | "Email" | "System";
  link?: { to: string; label: string };
  icon: typeof Sparkle;
}

const ACTIVITY_ROWS: ActivityRow[] = [
  {
    time: "Today · 09:00",
    title: "Reminder sent to Priya Patel",
    detail:
      "Wrike #4421 (VitaCore Final Artwork Sign-off) — 6 days unactioned. Slack DM + email; reply deadline 17:00 today before auto-reassign to Alex Drummond.",
    channel: "Slack",
    link: { to: "/tickets/682f00000000000000000202", label: "Open ticket" },
    icon: Sparkle,
  },
  {
    time: "Today · 08:52",
    title: "VitaCore 400g claim verified",
    detail:
      'Artwork "29.6g protein per serving" cross-checked against Reg Playbook §2 precedent (2026-02-14, T. Walsh). Rounding to 30g permitted with labelling-sheet amendment; routed back to Tom for amendment sign-off. 4 comments, all resolved.',
    channel: "System",
    link: { to: "/tickets/682f00000000000000000202", label: "Open ticket" },
    icon: ListChecks,
  },
  {
    time: "Today · 08:45",
    title: "Tom Walsh load assessed",
    detail:
      "11 overdue tasks · 8.4-day average response time. Suggested reassigning the protein claim check to Priya Patel (2.1-day avg, 1 active task).",
    channel: "System",
    icon: ChartLineUp,
  },
  {
    time: "Today · 08:30",
    title: "Luminos Social Brief flagged",
    detail:
      "Wrike comment posted on #4398 — missing target audience + asset dimensions per Brief Template §2 and §3, 2-day deadline for 11 assets auto-adjusted to 5 days. James Rivera + Sarah Chen cc'd.",
    channel: "Wrike",
    link: { to: "/tickets/682f00000000000000000206", label: "Open brief" },
    icon: ListChecks,
  },
  {
    time: "Yesterday · 17:00",
    title: "Luminos SPF30 routed",
    detail:
      "MEDIUM RISK · Visual ID Guide §2 ingredient-panel rule re-checked after Mia Okonkwo reupload (Vitamin B12 restored). Routed to James Rivera (Brand), then Priya Patel (Compliance). 2 comments resolved.",
    channel: "System",
    link: { to: "/agents/routing", label: "View routing timeline" },
    icon: ChartLineUp,
  },
  {
    time: "Yesterday · 16:50",
    title: "VitaCore 800g auto-approved",
    detail:
      "All 5 checks passed (Brand Bible §3.2, Reg Playbook §2 substantiation, Copy 7-word rule, Legal 7pt disclaimer, Dimensions). Stakeholders notified via Slack #vitalora-reviews; 2 follow-up comments resolved.",
    channel: "Slack",
    link: { to: "/tickets/682f00000000000000000201", label: "Open ticket" },
    icon: Check,
  },
];

interface RebalanceState {
  status: "open" | "confirmed";
}

export default function VitaloraAgentsActivity() {
  const [rebalance, setRebalance] = useState<RebalanceState>({
    status: "open",
  });

  return (
    <Box className="mx-auto w-full max-w-[1100px] px-6 py-8">
      <Flex align="center" justify="between" mb="5">
        <Box>
          <Heading size="6">Agent activity</Heading>
          <Text size="2" color="gray">
            Every action Puntt&apos;s agents took on the Vitalora workspace in
            the last 24 hours.
          </Text>
        </Box>
        <Link
          to="/agents/routing"
          className="text-sm text-puntt-accent-11 hover:underline"
        >
          View routing timeline →
        </Link>
      </Flex>

      <Flex direction="column" gap="3">
        {ACTIVITY_ROWS.map((row) => (
          <ActivityCard key={row.title} row={row} />
        ))}

        <RebalanceCard
          state={rebalance}
          onConfirm={() => setRebalance({ status: "confirmed" })}
        />
      </Flex>
    </Box>
  );
}

function ActivityCard({ row }: { row: ActivityRow }) {
  return (
    <div className="rounded-xl border border-puntt-neutral-gray-6 bg-base-white p-4 shadow-sm">
      <Flex gap="3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-puntt-accent-3 text-puntt-accent-11">
          <row.icon size={18} weight="duotone" />
        </div>
        <Flex direction="column" className="flex-1" gap="1">
          <Flex align="baseline" justify="between" gap="3">
            <Text weight="bold">{row.title}</Text>
            <Text size="1" color="gray">
              {row.time}
            </Text>
          </Flex>
          <Text size="2" color="gray">
            {row.detail}
          </Text>
          <Flex align="center" justify="between" mt="2">
            <Badge variant="soft" color="gray">
              {row.channel}
            </Badge>
            {row.link ? (
              <Link
                to={row.link.to}
                className="text-sm text-puntt-accent-11 hover:underline"
              >
                {row.link.label}
              </Link>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
}

function RebalanceCard({
  state,
  onConfirm,
}: {
  state: RebalanceState;
  onConfirm: () => void;
}) {
  return (
    <div className="border-puntt-warn-7 bg-puntt-warn-2 rounded-xl border p-4 shadow-sm">
      <Flex gap="3">
        <div className="bg-puntt-warn-3 text-puntt-warn-11 flex size-9 shrink-0 items-center justify-center rounded-full">
          <ChatCircleText size={18} weight="duotone" />
        </div>
        <Flex direction="column" className="flex-1" gap="2">
          <Flex align="baseline" justify="between">
            <Text weight="bold">Workload rebalance suggested</Text>
            <Text size="1" color="gray">
              Just now
            </Text>
          </Flex>
          <Text size="2">
            Tom Walsh: 11 overdue tasks, 8.4-day avg response. Reassign the
            VitaCore protein claim check to Priya Patel (2.1-day avg, 1 active
            task)?
          </Text>
          {state.status === "open" ? (
            <Flex gap="2" mt="1">
              <Button
                data-testid="activity-view-confirm-reassign"
                onClick={onConfirm}
              >
                <Check size={14} /> Confirm
              </Button>
            </Flex>
          ) : (
            <Flex gap="2" align="center" mt="1">
              <Badge color="green">
                <Check size={12} /> Reassigned to Priya Patel
              </Badge>
              <Text size="1" color="gray">
                ETA recalculated · launch date preserved.
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </div>
  );
}
